import { GetServerSidePropsContext } from "next";
import { useRouter } from "next/router";

import axios from "axios";

import { QueryClient } from "@enkor/react-query";

import GeneralHead from "@logics/tags/GeneralHead";
import { ResponseProjectDetail } from "@models/data-contracts";
import { ApiResponseDto } from "@models/client";
import { loadLocaleProps } from "@logics/utils/i18n";
import { useGuardWebView } from "@logics/hooks/useGuardWebView";
import { NAVIGATION_ROUTE } from "@domains/common/constants/navigationRoute";
import getTokenServerSession from "@domains/auth/utils/getTokenServerSession";
import ProjectDetailComponent from "@domains/project/views/ProjectDetail/ProjectDetail";
import { projectQueryKeys } from "@domains/project/constants/projectQueryKeys";

interface ProjectDetailProps {
  projectId: string;
  projectDetail?: ResponseProjectDetail;
}

const ProjectDetail = (props: ProjectDetailProps) => {
  const { replace } = useRouter();

  useGuardWebView({
    redirectCallback: () =>
      replace({
        href: NAVIGATION_ROUTE.HOME.HREF,
      }),
  });

  return (
    <>
      <GeneralHead
        page={{ title: `Enkoplay: ${props.projectDetail?.title}` }}
        openGraph={{ title: props.projectDetail?.title }}
      />

      <ProjectDetailComponent projectId={props.projectId} />
    </>
  );
};

export default ProjectDetail;

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const { projectId } = context.params as { projectId?: string };

  const tokenSession = getTokenServerSession({
    req: context.req,
    res: context.res,
    query: context.query,
  });

  if (!projectId) {
    return {
      redirect: {
        destination: NAVIGATION_ROUTE.EVENT_LIST.HREF,
        permanent: false,
      },
      props: {},
    };
  }

  const queryClient = new QueryClient();
  await queryClient.prefetchQuery({
    queryKey: projectQueryKeys.getProjectDetail({ projectId }),
    queryFn: async () => {
      const { data } = await axios.get<ApiResponseDto<ResponseProjectDetail>>(
        `${process.env.NEXT_PUBLIC_FIP_API_BASE_URL}/v1/project/${projectId}`,
      );
      return data.data;
    },
    retry: 0,
  });

  const projectDetailData = queryClient.getQueryData<ResponseProjectDetail>(
    projectQueryKeys.getProjectDetail({ projectId }),
  );

  return {
    ...(context.query.access && {
      redirect: {
        destination: tokenSession?.redirectUrl,
      },
    }),
    props: {
      ...(await loadLocaleProps(context)),
      projectId,
      projectDetail: projectDetailData || null,
    },
  };
}
